import { Box, Button, SlicedLine, Typography } from '@hermes/web-components'

import BaseModal from '@components/BaseModal'
import { DoubleButtons } from '@components/BaseModal.styles'
import BookImage from '@components/BookImage'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { AuthorLink } from 'containers/Product/ActionSection.styles'
import { AccordionLine } from 'containers/Product/DetailsSection.styles'

import { ListingDto } from '@hermes/domains'
import { BookConditionsEnum, ExtendedListing } from '@hermes/types'
import { useLocale } from '@hooks/useLocale'
import {
  BookDetailsBody,
  BookHeaderInfo,
  BookImageWrapper,
  BookTitle,
  DescriptionText
} from './BookDetailsModal.styles'

interface AlertsUnsubscribeProps {
  isOpen: boolean
  onClose: () => void
  listing: ListingDto | ExtendedListing
  actionLabel?: string
  actionDisabled?: boolean
  action: () => void
}

const BookDetailsModal = ({
  isOpen,
  onClose,
  listing,
  action,
  actionDisabled = false,
  actionLabel = 'add'
}: AlertsUnsubscribeProps) => {
  const { t } = useTranslateMessage()
  const locale = useLocale()

  const condition = {
    [BookConditionsEnum.NEW]: t({ id: 'common.new' }),
    [BookConditionsEnum.LIKE_NEW]: t({ id: 'common.like_new' }),
    [BookConditionsEnum.VERY_GOOD]: t({ id: 'common.very_good' }),
    [BookConditionsEnum.GOOD]: t({ id: 'common.good' }),
    [BookConditionsEnum.ACCEPTABLE]: t({ id: 'common.acceptable' }),
    [BookConditionsEnum.REFURBISHED]: t({ id: 'common.refurbished' })
  }

  const bookInfo = listing.book
  const book = {
    title: bookInfo.title,
    authors: bookInfo?.authors,
    description: bookInfo.description ?? '',
    image: bookInfo.fullImage || bookInfo.smallImage || undefined,
    lines: [
      { key: t({ id: 'bdp.info.condition' }), value: condition[listing.condition as keyof typeof condition] },
      { key: t({ id: 'bdp.info.price' }), value: t({ id: 'price' }, { price: listing.price }) },
      { key: t({ id: 'bdp.info.isbn' }), value: bookInfo.fullIsbn },
      { key: t({ id: 'format' }), value: t({ id: bookInfo.format }) },
      { key: t({ id: 'bdp.info.year' }), value: bookInfo.year },
      { key: t({ id: 'bdp.info.edition' }), value: bookInfo.editionNumber },
      { key: t({ id: 'bdp.info.originalTitle' }), value: bookInfo.title },
      { key: t({ id: 'bdp.info.subtitle' }), value: bookInfo.subtitle }
    ]
  }

  return (
    <BaseModal isOpen={isOpen} width={666} onClose={onClose} title={t({ id: 'bdp.bookDetails' })}>
      <BookDetailsBody>
        <BookHeaderInfo>
          <BookImageWrapper>
            <BookImage src={book.image} width={260} height={350} alt={book?.title} />
          </BookImageWrapper>
          <Box>
            <BookTitle>{book?.title}</BookTitle>
            {Boolean(book.authors.length) && (
              <Box display="flex" gap="5px" flexWrap="wrap">
                {book.authors.map((author, index) => (
                  <Typography key={author.id}>
                    <AuthorLink
                      href={`/${locale}/search?author=${author.id}&sortType=popular&initSearch=true&searchView=Grid`}
                    >{`${author.fullName}${index + 1 !== book.authors.length ? ', ' : ''}`}</AuthorLink>
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </BookHeaderInfo>
        {Boolean(book.description.length) && (
          <DescriptionText>
            <SlicedLine
              text={String(book?.description)}
              max={200}
              showMoreText={t({ id: 'showMore' }) as string}
              hideMoreText={t({ id: 'bdp.descriptionHide' }) as string}
              readMore
              triggerSx={{
                fontSize: '14px',
                color: 'primary.main'
              }}
            />
          </DescriptionText>
        )}
        {book.lines.map((info, index) => (
          <Box key={index}>
            {info.value && (
              <AccordionLine>
                <Typography fontWeight={500} fontSize="14px">
                  {info.key}
                </Typography>
                <Typography fontSize="14px" maxWidth="50%">
                  {info.value}
                </Typography>
              </AccordionLine>
            )}
          </Box>
        ))}
      </BookDetailsBody>
      <DoubleButtons>
        <Button onClick={onClose}>{t({ id: 'close' })}</Button>
        <Button
          disabled={actionDisabled}
          onClick={() => {
            action()
            onClose()
          }}
        >
          {actionLabel}
        </Button>
      </DoubleButtons>
    </BaseModal>
  )
}

export default BookDetailsModal
